import { useEffect, useMemo } from 'react'

import FileSaver from 'file-saver'
import { groupBy, kebabCase, sortBy } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'
import ReactMarkdown from 'react-markdown'
import Yaml from 'yaml'

import { scrollToTopScrollParent, useNavigation } from '@subaio/components'
import { usePage } from '@subaio/components/src/Router/usePage'
import { type Api, isNonNullable } from '@subaio/utils'

import { components } from 'components/DefaultMdxComponents'
import { Footer } from 'components/Page/Footer'

import { Container } from './Container'
import { type ApiContext, ApiContextProvider } from './context'
import { EndpointGroup } from './EndpointGroup'
import { Grid } from './Grid'
import { getRefValue } from './getRefValue'
import { Header } from './Header'
import { HeaderContent } from './HeaderContent'
import { isIdActive } from './isIdActive'
import { Models } from './Models'
import { Navigation } from './Navigation'

type Props = {
	apiDocument: OpenApi3.Document
	specToDownload?: OpenApi3.Document
	statuspageApi: Api
}

export const OpenApi = ({ apiDocument, specToDownload = apiDocument, statuspageApi }: Props) => {
	const { name: pageName, params: pageParams, fragment } = usePage()!
	const { onNavigateTo } = useNavigation()

	const tags = useMemo(
		() =>
			Object.entries(
				groupBy(Object.entries(apiDocument.paths!), ([, value]) => {
					if (!value) return
					const method = Object.values(value)[0]!
					if (typeof method !== 'string' && 'tags' in method) return method.tags
				}),
			).map(([title, paths]) => {
				const tag = apiDocument.tags?.find(({ name }) => name === title)
				const tagTitle = tag?.name ?? title

				return {
					title: tag?.name ?? title,
					id: kebabCase(tagTitle),
					description: tag?.description,
					paths: paths
						.map(([path]) => {
							if (!apiDocument.paths?.[path]) return null

							return {
								path,
								methods: Object.entries(apiDocument.paths[path]!)
									.map(([key, value]) =>
										typeof value !== 'string' && 'tags' in value
											? {
													method: key,
													title: value.summary!,
													id: kebabCase(`${tagTitle}-${value.operationId!}`),
													description: value.description,
												}
											: null,
									)
									.filter(isNonNullable),
							}
						})
						.filter(isNonNullable),
				}
			}),
		[apiDocument],
	)

	const models = useMemo(
		() =>
			apiDocument.components?.schemas
				? {
						title: 'Models',
						id: 'models',
						description: 'A list of all the models provided by the spec',
						names: sortBy(Object.keys(apiDocument.components.schemas), (name) => name).map((name) => name),
					}
				: null,
		[apiDocument.components?.schemas],
	)

	useEffect(() => {
		const target = fragment ? document.getElementById(fragment) : null
		if (target) {
			scrollToTopScrollParent(target, { behavior: 'smooth' })
		}
	}, [fragment])

	const headings = useMemo(() => {
		const headingsFromTags = tags.map(({ title, description, id, paths }) => ({
			title,
			description,
			id,
			paths: paths.flatMap(({ methods }) => methods),
		}))

		if (models) {
			return [
				...headingsFromTags,
				{
					...models,
					paths: models.names.map((name) => ({
						title: name,
						id: `${models.title}-${name}`.toLowerCase(),
						method: '',
					})),
				},
			]
		}

		return headingsFromTags
	}, [tags, models])

	const apiContext = useMemo<ApiContext>(
		() => ({
			components: {
				schemas: apiDocument.components?.schemas ?? {},
				parameters: apiDocument.components?.parameters ?? {},
				headers: apiDocument.components?.headers ?? {},
				responses: apiDocument.components?.responses ?? {},
			},
			getRefValue: (ref) => getRefValue({ ...apiDocument.components, ref }),
		}),
		[apiDocument],
	)

	return (
		<ApiContextProvider value={apiContext}>
			<Grid>
				<Navigation
					pageName={pageName}
					pageParams={pageParams}
					fragment={fragment}
					title={apiDocument.info.title}
					headings={headings}
					onDownloadJson={() => {
						FileSaver.saveAs(
							new File(
								[new TextEncoder().encode(JSON.stringify(specToDownload, null, 2))],
								`${kebabCase(apiDocument.info.title)}.json`,
								{ type: 'application/json;charset=utf-8' },
							),
						)
					}}
					onDownloadYaml={() => {
						FileSaver.saveAs(
							new File([Yaml.stringify(specToDownload)], `${kebabCase(apiDocument.info.title)}.yaml`, {
								type: 'application/x-yaml;charset=utf-8',
							}),
						)
					}}
				/>
				<Container>
					<Header>
						<HeaderContent>
							<h1>{apiDocument.info.title}</h1>
							{apiDocument.info.description && (
								<ReactMarkdown components={components}>{apiDocument.info.description}</ReactMarkdown>
							)}
						</HeaderContent>
					</Header>
					{tags.map((path, index) => (
						<EndpointGroup
							{...path}
							key={index}
							isOpen={
								isIdActive(fragment, path.id) ||
								(tags.length === 1 && Boolean(models) && !isIdActive(fragment, models!.id))
							}
							apiDocument={apiDocument}
							onSetIsOpen={() => {
								onNavigateTo((page) => ({ ...page, fragment: path.id }))
							}}
						/>
					))}
					{apiDocument.components?.schemas && models && (
						<Models
							title={models.title}
							description={models.description}
							isOpen={isIdActive(fragment, models.id)}
							apiDocument={apiDocument}
							definitions={models.names}
							onSetIsOpen={() => {
								onNavigateTo((page) => ({ ...page, fragment: models.id }))
							}}
						/>
					)}
					<Footer statuspageApi={statuspageApi} />
				</Container>
			</Grid>
		</ApiContextProvider>
	)
}
